import React, { useContext, useState, useEffect, useMemo } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { encodeDate } from '@campfire/hot-date';

import { useCampfireLazyQuery } from '../../../../global/network/useCampfireLazyQuery';
import { useUser } from '../../../../global/auth/useUser';
import { useDefaultRosterStartDate } from '../../../../global/auth/use-org-info/use-default-roster-start-date';
import { GET_MY_ELEMENTS_SIDEBAR_COMBINED_DATA } from './sidebar/sidebar-combined-model.gql';
import {
  GetMyElementsSidebarCombinedData,
  GetMyElementsSidebarCombinedDataVariables,
  GetMyElementsSidebarCombinedData_vm_volunteer_profile as SidebarProfile,
  GetMyElementsSidebarCombinedData_vm_volunteer_activityEnrolments as ActivityEnrolment,
  GetMyElementsSidebarCombinedData_vm_tasks as Task,
  GetMyElementsSidebarCombinedData_vm_bulletins as Bulletin,
  GetMyElementsSidebarCombinedData_vm_mostRecentBulletin as RecentBulletin,
  GetMyElementsSidebarCombinedData_vm_volunteer_upcomingRosters as UpcomingRoster,
} from './sidebar/__generated__/GetMyElementsSidebarCombinedData';
import { useMyAchievements } from './my-achievements/useMyAchievements';

interface VolDashboardMyElementsContextInterface {
  // Consolidated sidebar data
  sidebarData: GetMyElementsSidebarCombinedData | undefined;
  sidebarLoading: boolean;
  sidebarRefetch: (() => Promise<ApolloQueryResult<GetMyElementsSidebarCombinedData>>) | undefined;

  // Derived data for convenience
  profile: SidebarProfile | null | undefined;
  activityEnrolments: ActivityEnrolment[] | undefined;
  tasks: Task[] | undefined;
  bulletins: Bulletin[] | undefined;
  mostRecentBulletin: RecentBulletin | null | undefined;
  upcomingRosters: UpcomingRoster[] | undefined;
  orgName: string | undefined;

  // Computed values
  newTasksNumber: number;
  setNewTasksNumber: (x: number) => void;
  upcomingRosterNumber: number;
  setUpcomingRosterNumber: (x: number) => void;
  unreadNewsCount: number;

  // UI state
  selectedNewsId?: string;
  setSelectedNewsId: (x?: string) => void;
  viewMode: string;
  setViewMode: (x: string) => void;

  myAchievements: ReturnType<typeof useMyAchievements>;
  newAchievementIds?: Set<string>;
  setNewAchievementIds: (x: Set<string>) => void;
}

interface VolDashboardMyElementsProviderProps {
  children: React.ReactNode;
}

const VolDashboardMyElementsContext = React.createContext<VolDashboardMyElementsContextInterface | undefined>(
  undefined
);

const VolDashboardMyElementsProvider = (props: VolDashboardMyElementsProviderProps) => {
  const { getVolunteerIdentity } = useUser();
  const volunteerIdentity = getVolunteerIdentity();
  const defaultRosterStartDate = useDefaultRosterStartDate();

  const [newTasksNumber, setNewTasksNumber] = useState<number>(0);
  const [selectedNewsId, setSelectedNewsId] = useState<string>();
  const [viewMode, setViewMode] = useState<string>('all');
  const [upcomingRosterNumber, setUpcomingRosterNumber] = useState<number>(0);
  const myAchievements = useMyAchievements();
  const [newAchievementIds, setNewAchievementIds] = useState<Set<string>>();

  // Single consolidated query for all sidebar data
  const [
    fetchSidebarData,
    { data: sidebarData, loading: sidebarLoading, refetch: sidebarRefetch },
  ] = useCampfireLazyQuery<GetMyElementsSidebarCombinedData, GetMyElementsSidebarCombinedDataVariables>(
    GET_MY_ELEMENTS_SIDEBAR_COMBINED_DATA
  );

  // Fetch sidebar data on mount
  useEffect(() => {
    const startDate = encodeDate(defaultRosterStartDate);
    const endDate = encodeDate(defaultRosterStartDate.plus({ weeks: 2 }));

    fetchSidebarData({
      variables: {
        volunteerId: volunteerIdentity.volunteerId,
        startDate,
        endDate,
      },
    });
  }, [volunteerIdentity.volunteerId, defaultRosterStartDate]);

  // Derive data from consolidated response
  const profile = sidebarData?.vm.volunteer?.profile;
  const activityEnrolments = sidebarData?.vm.volunteer?.activityEnrolments;
  const tasks = sidebarData?.vm.tasks;
  const bulletins = sidebarData?.vm.bulletins;
  const mostRecentBulletin = sidebarData?.vm.mostRecentBulletin;
  const upcomingRosters = sidebarData?.vm.volunteer?.upcomingRosters;
  const orgName = sidebarData?.orgName;

  // Compute unread news count
  const unreadNewsCount = useMemo(() => {
    return bulletins?.filter((b) => !b.hasVolunteerViewedBulletin).length ?? 0;
  }, [bulletins]);

  // Compute new tasks number
  useEffect(() => {
    if (sidebarData) {
      const myIncompleteTasks =
        tasks
          ?.filter(
            (task) =>
              !profile?.completedTasks.find((ct) => ct.task.taskId === task.taskId) && task.cake.cakeType === 'required'
          )
          .filter((task) => !task.dateRemoved) ?? [];
      setNewTasksNumber(myIncompleteTasks.length);
    }
  }, [sidebarData, tasks, profile]);

  // Compute upcoming roster number
  useEffect(() => {
    if (upcomingRosters) {
      const availabilityCount = upcomingRosters.filter(
        (roster) => roster.__typename === 'VOLUNTEER_UpcomingRosterUnspecifiedType'
      ).length;
      setUpcomingRosterNumber(availabilityCount);
    }
  }, [upcomingRosters]);

  const value = {
    // Consolidated data
    sidebarData,
    sidebarLoading,
    sidebarRefetch,

    // Derived data
    profile,
    activityEnrolments,
    tasks,
    bulletins,
    mostRecentBulletin,
    upcomingRosters,
    orgName,

    // Computed values
    newTasksNumber,
    setNewTasksNumber,
    upcomingRosterNumber,
    setUpcomingRosterNumber,
    unreadNewsCount,

    // UI state
    selectedNewsId,
    setSelectedNewsId,
    viewMode,
    setViewMode,

    myAchievements,
    newAchievementIds,
    setNewAchievementIds,
  };

  return (
    <VolDashboardMyElementsContext.Provider value={value} {...props}>
      {props.children}
    </VolDashboardMyElementsContext.Provider>
  );
};

export const useVolDashboardMyElementsContext = () => {
  const value = useContext(VolDashboardMyElementsContext);
  if (!value) throw new Error('Must be inside of a VolDashboardMyElementsProvider');
  return value;
};

export { VolDashboardMyElementsProvider };
